/*
 * cart-minimum.js
 * Enforces a minimum total quantity in the cart from the theme side, so the
 * customer can never sit on a cart with fewer than N units (default 2) — even
 * inside a third-party cart drawer (Candyrack) whose markup we don't control.
 *
 * How it works (no DOM coupling to the drawer):
 *   - We wrap window.fetch and XMLHttpRequest and watch for any completed
 *     mutation to the standard Shopify cart endpoints (/cart/add|change|update|
 *     clear, with or without `.js`). Every cart drawer — theme or app — goes
 *     through these under the hood.
 *   - After a mutation lands, we read /cart.js. If 0 < item_count < min, we
 *     raise the first line's quantity by the deficit via /cart/change so the
 *     cart total is exactly `min`. An empty cart (0) is left alone.
 *   - The correction is sent through the normal (app-wrapped) fetch so the
 *     drawer re-renders and shows the corrected quantity.
 *
 * This is a UX/theme-level guard. It is intentionally NOT a hard guarantee —
 * a determined user can still bypass it (JS off, direct API). For an
 * unbypassable floor, add a Shopify Cart/Checkout Validation Function.
 */
(function () {
  var config = window.__cartMin || {};
  var MIN = parseInt(config.min, 10) || 2;
  if (config.enabled === false || MIN < 2) return;

  var root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Per-product minimum: listed products must keep at least PRODUCT_MIN units in
  // the cart on their own, regardless of the whole-cart total or other lines.
  var PRODUCT_IDS = (config.productIds || []).map(String);
  var PRODUCT_MIN = parseInt(config.productMin, 10) || 0;

  // If a cart line is one of the listed products, return its required minimum.
  // Matches on product id or variant id, so either kind of id in the setting
  // works.
  function productMinFor(item) {
    if (!PRODUCT_MIN || !PRODUCT_IDS.length) return 0;
    var ids = [String(item.product_id), String(item.id), String(item.variant_id)];
    for (var i = 0; i < PRODUCT_IDS.length; i++) {
      if (ids.indexOf(PRODUCT_IDS[i]) !== -1) return PRODUCT_MIN;
    }
    return 0;
  }

  // Build the set of line corrections needed to satisfy BOTH rules:
  //   1. each listed product ≥ its per-product minimum, then
  //   2. the whole cart ≥ MIN (top up the first line if still short).
  // Keyed by line item `key` so duplicate variants stay unambiguous.
  function computeUpdates(cart) {
    var items = cart.items || [];
    if (!items.length) return {};
    var updates = {};
    var i, it, qty;

    for (i = 0; i < items.length; i++) {
      it = items[i];
      var pmin = productMinFor(it);
      if (pmin && (it.quantity || 0) < pmin) updates[it.key] = pmin;
    }

    var total = 0;
    for (i = 0; i < items.length; i++) {
      it = items[i];
      qty = updates[it.key] != null ? updates[it.key] : it.quantity || 0;
      total += qty;
    }
    if (total > 0 && total < MIN) {
      var first = items[0];
      var curr = updates[first.key] != null ? updates[first.key] : first.quantity || 0;
      updates[first.key] = curr + (MIN - total);
    }
    return updates;
  }

  /* --------------------- subscription sync (add-ons) --------------------- */
  // Every non-main, non-gift line follows the main product's purchase type:
  // main is a subscription -> add-ons become subscriptions (their own default
  // plan); main is one-time -> add-ons are stripped back to one-time.
  var SUB = config.subSync || {};
  var SUB_MAIN_ID = SUB.mainId != null ? String(SUB.mainId) : null;
  var SUB_ENABLED = !!SUB.enabled && !!SUB_MAIN_ID;

  function isMainLine(item) {
    if (!SUB_MAIN_ID) return false;
    return (
      String(item.product_id) === SUB_MAIN_ID ||
      String(item.variant_id) === SUB_MAIN_ID ||
      String(item.id) === SUB_MAIN_ID
    );
  }

  function isGiftLine(item) {
    var p = item.properties;
    // Auto-added gifts carry a _gift property; never force them onto a plan.
    return !!(p && (p._gift || p['_gift'] || p.gift));
  }

  // Cache /products/<handle>.js so repeated syncs don't refetch.
  var productCache = {};
  function productJson(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    var p = fetch(root + 'products/' + handle + '.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
    productCache[handle] = p;
    return p;
  }

  // First (default) selling-plan id available for the line's variant, or null
  // when the product has no subscription plan.
  function defaultPlanFor(item) {
    return productJson(item.handle).then(function (prod) {
      if (!prod || !prod.variants) return null;
      var vid = String(item.variant_id != null ? item.variant_id : item.id);
      for (var i = 0; i < prod.variants.length; i++) {
        if (String(prod.variants[i].id) === vid) {
          var allocs = prod.variants[i].selling_plan_allocations;
          return allocs && allocs.length ? allocs[0].selling_plan_id : null;
        }
      }
      return null;
    });
  }

  // Build the /cart/change.js payloads that align every add-on line with the
  // main product's purchase type. Resolves to [] when disabled or main absent.
  function computeSubChanges(cart) {
    if (!SUB_ENABLED) return Promise.resolve([]);
    var items = cart.items || [];
    var mainFound = false;
    var mainOn = false;
    var i;
    for (i = 0; i < items.length; i++) {
      if (isMainLine(items[i])) {
        mainFound = true;
        if (items[i].selling_plan_allocation) mainOn = true;
      }
    }
    if (!mainFound) return Promise.resolve([]); // main not in cart -> leave others

    var changes = [];
    var pending = [];
    for (i = 0; i < items.length; i++) {
      var it = items[i];
      if (isMainLine(it) || isGiftLine(it)) continue;
      var line = i + 1; // 1-based; required when changing selling_plan
      var hasPlan = !!it.selling_plan_allocation;
      if (!mainOn) {
        if (hasPlan) changes.push({ line: line, quantity: it.quantity, selling_plan: null });
      } else if (!hasPlan) {
        (function (item, lineNo) {
          pending.push(
            defaultPlanFor(item).then(function (planId) {
              if (planId) {
                changes.push({ line: lineNo, quantity: item.quantity, selling_plan: planId });
              } else {
                console.warn(
                  '[cart-minimum] Sub-sync: "' + item.handle + '" has no selling plan; left one-time.'
                );
              }
            })
          );
        })(it, line);
      }
    }
    return Promise.all(pending).then(function () {
      return changes;
    });
  }

  // Matches …/cart/add | change | update | clear, optionally `.js`, ignoring
  // any query string. Covers both routes.cart_change_url (/cart/change) and the
  // Ajax `.js` variants apps use.
  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  var state = {
    repairing: false, // guard so our own correction doesn't re-trigger a check
    pending: false, // a check is scheduled/in-flight
    targetIndexes: [], // 0-based cart-line indexes that match a per-product min
    cartLineCount: 0, // number of cart lines at the last check
  };

  // Record WHICH cart lines are per-product-min targets, by their position in
  // the cart. Candyrack renders lines in cart order, so the Nth drawer line maps
  // to the Nth cart line — far more reliable than guessing from product links,
  // which mis-identified sibling lines.
  function updateTargets(cart) {
    var items = cart.items || [];
    var idxs = [];
    for (var i = 0; i < items.length; i++) {
      if (productMinFor(items[i])) idxs.push(i);
    }
    state.targetIndexes = idxs;
    state.cartLineCount = items.length;
  }

  function setBelowMin(on) {
    try {
      document.documentElement.classList.toggle('cart-below-min', !!on);
    } catch (e) {
      /* pre-DOM — ignore, the next check re-applies */
    }
  }

  /* ------------ per-line stepper: disable "-" at/under the min ------------ */
  // Exact Candyrack classes so each selector yields exactly ONE element per cart
  // line, in cart order — required for the index → cart-line mapping below.
  // Override via window.__cartMin.minusSelector / .removeSelector if needed.
  var MINUS_SELECTOR =
    config.minusSelector || '.candyrack-slider-cart-item-product-details-quantity-button-minus';

  // Remove / delete (trash) button on a cart line.
  var REMOVE_SELECTOR =
    config.removeSelector || '.candyrack-slider-cart-item-product-details-remove-button';

  // Read the quantity shown next to a given minus button. The stepper container
  // is the minus button's parent (holds − value +); the price sits outside it,
  // so the first integer in the container's text is the line quantity.
  function stepperQty(minusBtn) {
    var wrap = minusBtn.parentElement;
    if (!wrap) return null;
    var input = wrap.querySelector('input');
    if (input && input.value !== '' && input.value != null) {
      var vi = parseInt(input.value, 10);
      if (!isNaN(vi)) return vi;
    }
    var valueEl = wrap.querySelector(
      '[class*="quantity-input"],[class*="quantity-value"],[class*="quantity-count"],[class*="qty-value"]'
    );
    var text = valueEl ? valueEl.textContent : wrap.textContent;
    var digits = (text || '').match(/\d+/);
    return digits ? parseInt(digits[0], 10) : null;
  }

  // Are the drawer's line buttons aligned 1:1 with the cart lines? Only then can
  // we trust the index → cart-line mapping. If counts differ (a stray/template
  // row, or a mismatched selector), we skip per-product locks rather than risk
  // hiding controls on the wrong line.
  function indexAligned(buttonCount) {
    return (
      state.cartLineCount > 0 &&
      buttonCount === state.cartLineCount &&
      (state.targetIndexes || []).length > 0
    );
  }

  function enforceSteppers() {
    var buttons;
    try {
      buttons = document.querySelectorAll(MINUS_SELECTOR);
    } catch (e) {
      return;
    }
    if (!buttons.length) return;

    // Two locks:
    //  - WHOLE-CART: sum of every stepper; when the combined total sits at the
    //    floor, disable all "-" (a cart of 2×2 = 4 keeps them active).
    //  - PER-PRODUCT: a target line's own "-" is disabled once it's at its
    //    minimum, regardless of the cart total. Target lines are identified by
    //    index (button N ↔ cart line N).
    var qtys = [];
    var total = 0;
    var i;
    for (i = 0; i < buttons.length; i++) {
      var q = stepperQty(buttons[i]);
      qtys.push(q);
      if (q != null) total += q;
    }
    var wholeCartLock = total > 0 && total <= MIN;
    var perProduct = PRODUCT_MIN && indexAligned(buttons.length);
    var targets = state.targetIndexes || [];

    for (i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var lock = wholeCartLock;
      if (!lock && perProduct && targets.indexOf(i) !== -1) {
        var qty = qtys[i];
        if (qty != null && qty <= PRODUCT_MIN) lock = true;
      }
      btn.classList.toggle('cart-min-nominus', lock);
      if ('disabled' in btn) btn.disabled = lock; // real <button>
      btn.setAttribute('aria-disabled', lock ? 'true' : 'false');
    }

    enforceRemoveButtons();
  }

  // Hide the delete (trash) icon ONLY on target lines (matched by index) — those
  // products must stay in the cart, so they can't be removed outright. Every
  // other line keeps its trash icon.
  function enforceRemoveButtons() {
    var buttons;
    try {
      buttons = document.querySelectorAll(REMOVE_SELECTOR);
    } catch (e) {
      return;
    }
    var aligned = indexAligned(buttons.length);
    var targets = state.targetIndexes || [];
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].classList.toggle('cart-min-noremove', aligned && targets.indexOf(i) !== -1);
    }
  }

  // Re-apply after the app re-renders the cart DOM.
  var stepperObserver = null;
  function startStepperObserver() {
    if (stepperObserver || !window.MutationObserver || !document.body) return;
    var t;
    stepperObserver = new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(enforceSteppers, 100);
    });
    stepperObserver.observe(document.body, { childList: true, subtree: true });
  }

  function isMutation(url) {
    if (!url) return false;
    try {
      return MUTATION_RE.test(String(url));
    } catch (e) {
      return false;
    }
  }

  function scheduleCheck() {
    if (state.repairing || state.pending) return;
    state.pending = true;
    // Coalesce bursts (a drawer may fire change + section re-render together)
    // and let the app finish its own handling first.
    setTimeout(runCheck, 60);
  }

  function cartPost(path, body) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  function runCheck() {
    fetch(root + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var total = cart.item_count || 0;
        // Learn which product handle/variant in the cart is a per-product target
        // so enforceSteppers can identify its line in the drawer DOM.
        updateTargets(cart);
        // Toggle the flag that hides the drawer's remove icon while the cart is
        // below the minimum (see snippets/cart-minimum.liquid). Ancestor class
        // on <html>, so it survives the app re-rendering the cart DOM.
        setBelowMin(total > 0 && total < MIN);
        enforceSteppers();
        reconcile(cart);
      })
      .catch(function () {
        state.pending = false;
      });
  }

  // One guarded pass: (1) minimum-quantity corrections via /cart/update.js,
  // then (2) subscription sync via /cart/change.js so add-on lines match the
  // main product's purchase type. Converges — after the pass the cart already
  // matches, so the next check finds nothing to do.
  function reconcile(cart) {
    var qtyUpdates = computeUpdates(cart);
    computeSubChanges(cart)
      .then(function (subChanges) {
        var needQty = Object.keys(qtyUpdates).length > 0;
        if (!needQty && !subChanges.length) {
          state.pending = false;
          return;
        }
        state.repairing = true;
        var chain = Promise.resolve();
        if (needQty) {
          chain = chain.then(function () {
            return cartPost('cart/update.js', { updates: qtyUpdates });
          });
        }
        subChanges.forEach(function (ch) {
          chain = chain.then(function () {
            return cartPost('cart/change.js', ch);
          });
        });
        return chain
          .then(function () {
            notifyThemeCart();
            setBelowMin(false);
            enforceSteppers();
          })
          .catch(function () {})
          .then(function () {
            // Release guards on the next tick so the app's own listeners for
            // our corrections settle, then reconcile the UI once more.
            setTimeout(function () {
              state.repairing = false;
              state.pending = false;
              setBelowMin(false);
              enforceSteppers();
            }, 80);
          });
      })
      .catch(function () {
        state.pending = false;
      });
  }

  // Nudge Dawn/Shrine components (and any pub/sub listeners) to refresh after
  // our correction, in addition to the app drawer reacting to the fetch itself.
  function notifyThemeCart() {
    try {
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-minimum' });
      }
    } catch (e) {
      /* pub/sub not available — the app drawer still re-renders from the fetch */
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {
      /* older browsers — ignore */
    }
  }

  /* ----------------------------- fetch hook ----------------------------- */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var method =
        (init && init.method) ||
        (input && input.method) ||
        'GET';
      var watch = !state.repairing && isMutation(url) && String(method).toUpperCase() !== 'GET';
      var result = nativeFetch.apply(this, arguments);
      if (watch && result && typeof result.then === 'function') {
        result.then(function () {
          scheduleCheck();
        }, function () {});
      }
      return result;
    };
  }

  /* ------------------------------ XHR hook ------------------------------ */
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    var nativeOpen = XHR.prototype.open;
    var nativeSend = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      this.__cartMinWatch =
        String(method || '').toUpperCase() !== 'GET' && isMutation(url);
      return nativeOpen.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      if (this.__cartMinWatch && !state.repairing) {
        this.addEventListener('loadend', function () {
          if (this.status >= 200 && this.status < 300) scheduleCheck();
        });
      }
      return nativeSend.apply(this, arguments);
    };
  }

  // Safety net: enforce once on load in case the cart already holds a single
  // unit from a previous session, and start watching for drawer re-renders.
  function init() {
    startStepperObserver();
    enforceSteppers();
    scheduleCheck();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
